import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { 
  Users, 
  Plus, 
  Building, 
  CheckCircle, 
  XCircle,
  Clock,
  Settings,
  ShieldAlert,
  Loader2,
  Star,
  Calendar,
  TrendingUp,
  Target,
  BarChart
} from 'lucide-react';
import { JoinRequestsList } from '@/components/company/join-requests-list';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    users: 0,
    clients: 0,
    prospects: 0,
    pastClients: 0,
    verifiedJobs: 0,
    userNps: 0,
    clientNps: 0,
    interviews: 0,
    passthrough: 0
  });

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      // Get user's email
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      // Check if user has admin access
      const isAdmin = authUser?.email ? ['jonathan@newloop.ai', 'admin@newloop.ai', 'support@newloop.ai'].includes(authUser.email) : false;
      
      if (!isAdmin) {
        toast({
          variant: 'destructive',
          title: 'Access Denied',
          description: 'You do not have permission to access this page.'
        });
        navigate('/dashboard');
        return;
      }

      fetchDashboardData();
    };

    checkAdminAccess();
  }, [user, navigate, toast]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch metrics using Promise.all for parallel requests
      const [
        userCount, 
        clientCount, 
        prospectCount,
        jobCount, 
        interviewCount
      ] = await Promise.all([
        // Get total users
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .then(({ count }) => count || 0),
        
        // Get client companies from clients table
        supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'client')
          .then(({ count }) => count || 0),
        
        // Get prospect companies from clients table
        supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'prospect')
          .then(({ count }) => count || 0),
        
        // Get total jobs
        supabase
          .from('recruiting_projects')
          .select('*', { count: 'exact', head: true })
          .then(({ count }) => count || 0),
        
        // Get total interviews
        supabase
          .from('interviews')
          .select('*', { count: 'exact', head: true })
          .then(({ count }) => count || 0)
      ]);

      setMetrics({
        users: userCount,
        clients: clientCount,
        prospects: prospectCount,
        pastClients: 0, // This is now handled by the clients table status
        verifiedJobs: jobCount,
        userNps: 85, // Static value for now
        clientNps: 90, // Static value for now
        interviews: interviewCount,
        passthrough: 75 // Static value for now
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load some dashboard data'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            </div>
            <p className="text-muted-foreground">
              Monitor platform performance and manage system settings
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Exit Admin Mode
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate('/admin/talent')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                  <h3 className="text-2xl font-bold mt-2">{metrics.users}</h3>
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate('/admin/clients')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Active Clients</p>
                  <h3 className="text-2xl font-bold mt-2">{metrics.clients}</h3>
                  <div className="flex gap-2 mt-1">
                    <Badge variant="secondary">{metrics.prospects} prospects</Badge>
                    <Badge variant="outline">{metrics.pastClients} past</Badge>
                  </div>
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Building className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Verified Jobs</p>
                  <h3 className="text-2xl font-bold mt-2">{metrics.verifiedJobs}</h3>
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Interviews</p>
                  <h3 className="text-2xl font-bold mt-2">{metrics.interviews}</h3>
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>User Satisfaction</CardTitle>
              <CardDescription>NPS scores and user feedback</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">User NPS</span>
                  <span className="font-medium">{metrics.userNps}</span>
                </div>
                <Progress value={metrics.userNps} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Client NPS</span>
                  <span className="font-medium">{metrics.clientNps}</span>
                </div>
                <Progress value={metrics.clientNps} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Key platform performance indicators</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">AI Match Accuracy</span>
                  <span className="font-medium">92%</span>
                </div>
                <Progress value={92} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Interview Passthrough</span>
                  <span className="font-medium">{metrics.passthrough}%</span>
                </div>
                <Progress value={metrics.passthrough} className="h-2" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Join Requests List */}
        <JoinRequestsList />
      </div>
    </div>
  );
}